import { useState, useEffect, useContext, useMemo } from "react";
import dayjs from "dayjs";

import { makeStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";

import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import DataUsageOutlinedIcon from "@mui/icons-material/DataUsageOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";

// My Files
import { DatabaseContext } from "../../../../context";
import HubUsageUserFilterDialog from "./filters/HubUsageUserFilterDialog";
import HubUsageGroupFilterDialog from "./filters/HubUsageGroupFilterDialog";
import HubUsageDateFilterDialog from "./filters/HubUsageDateFilterDialog";
import { downloadBlob } from "../../../../utility";

const useStyles = makeStyles((theme) => ({
    root: {
        textAlign: "center",
        alignItems: "center",
    },
    toolbarTitle: {
        // flexGrow: 1,
    },
    title: {
        marginTop: 15,
    },
    tableContainer: {
        marginBottom: 10,
        maxHeight: window.innerHeight - 255,
        width: window.innerWidth,
        overflow: "scroll",
    },
    tableHead: {
        background: theme.palette.primary.main,
        color: "white",
    },
    tableHeaderCell: {
        // The head row sits on primary.main in both palettes, so white ink and a
        // palette divider stay legible either way.
        color: "white",
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    loadingContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 400,
        gap: 20,
    },
}));

export default function HubUserUsageDialog(props) {
    const classes = useStyles();
    const { users, files, userLogins, groups, getFileUsage } =
        useContext(DatabaseContext);

    const [userFilterDialogIsOpen, setUserFilterDialogIsOpen] = useState(false);
    const [groupFilterDialogIsOpen, setGroupFilterDialogIsOpen] =
        useState(false);
    const [dateFilterDialogIsOpen, setDateFilterDialogIsOpen] = useState(false);

    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadedFileUsage, setLoadedFileUsage] = useState(null);
    const [loadedHubKey, setLoadedHubKey] = useState(null);
    const [userSearch, setUserSearch] = useState("");

    const hubKey = props.hub ? props.hub.key : null;

    const [filterStartDate, setFilterStartDate] = useState(null);
    const [filterEndDate, setFilterEndDate] = useState(null);
    const [hubUsers, setHubUsers] = useState([]);
    const [hubGroups, setHubGroups] = useState([]);

    const [
        excludedUserKeysFromUsersFilter,
        setExcludedUserKeysFromUsersFilter,
    ] = useState([]);
    const [
        excludedUserKeysFromGroupsFilter,
        setExcludedUserKeysFromGroupsFilter,
    ] = useState([]);

    const [filterIsActive, setFilterIsActive] = useState(false);

    // Create lookup maps for better performance
    const userLoginMap = useMemo(() => {
        const map = new Map();
        userLogins.forEach((login) => {
            map.set(login.key, login.lastLogin);
        });
        return map;
    }, [userLogins]);

    const userMap = useMemo(() => {
        const map = new Map();
        users.forEach((user) => {
            map.set(user.key, user);
        });
        return map;
    }, [users]);

    const groupMap = useMemo(() => {
        const map = new Map();
        groups.forEach((group) => {
            map.set(group.key, group);
        });
        return map;
    }, [groups]);

    useEffect(() => {
        let isActive = true;

        if (!props.open || !hubKey) {
            setIsLoading(false);
            setLoadedFileUsage(null);
            setLoadedHubKey(null);
            setRows([]);
            return () => {
                isActive = false;
            };
        }

        setIsLoading(true);
        setLoadedFileUsage(null);
        setLoadedHubKey(null);
        setRows([]);

        getFileUsage(true).then((usageData) => {
            if (!isActive) return;

            setLoadedFileUsage(usageData);
            setLoadedHubKey(hubKey);
        });

        return () => {
            isActive = false;
        };
    }, [props.open, hubKey, getFileUsage]);

    useEffect(() => {
        if (
            !props.hub ||
            !props.open ||
            loadedFileUsage === null ||
            loadedHubKey !== props.hub.key
        ) {
            return;
        }

        const compileData = () => {
            setIsLoading(true);

            // Use setTimeout to allow UI to update with loading indicator
            return setTimeout(() => {
                try {
                    console.log("Compiling Hub User Usage Data:");

                    let filesInHub = files.filter((file) =>
                        file.ancestors.includes(props.hub.key)
                    );

                    // Create a Set of file keys for faster lookup
                    const fileKeysSet = new Set(filesInHub.map((f) => f.key));

                    const hubGroupsArray = groups.filter(
                        (g) => g.hub === props.hub.key && g.name !== "All Users"
                    );
                    setHubGroups(hubGroupsArray);

                    // Deduplicate user keys
                    let allUserKeys = [
                        ...new Set([...props.hub.users, ...props.hub.managers]),
                    ];

                    // Build usage map for faster lookups: Map<userKey, Map<fileKey, usage[]>>
                    const usageMap = new Map();
                    loadedFileUsage.forEach((usage) => {
                        if (fileKeysSet.has(usage.file)) {
                            if (!usageMap.has(usage.user)) {
                                usageMap.set(usage.user, new Map());
                            }
                            const userUsageMap = usageMap.get(usage.user);
                            if (!userUsageMap.has(usage.file)) {
                                userUsageMap.set(usage.file, []);
                            }
                            userUsageMap.get(usage.file).push(usage);
                        }
                    });

                    var hubUsersArray = [];
                    let rowDataArray = [];

                    // Process each user
                    allUserKeys.forEach((userKey) => {
                        let user = userMap.get(userKey);
                        if (!user) return;

                        hubUsersArray.push(user);

                        // Check if user should be excluded based on filters
                        if (
                            excludedUserKeysFromUsersFilter.includes(
                                user.key
                            ) ||
                            excludedUserKeysFromGroupsFilter.includes(user.key)
                        ) {
                            return;
                        }

                        // Last Login
                        const lastLogin = userLoginMap.get(user.key) || "";

                        // Initialize counters
                        let viewCount = 0;
                        let downloadCount = 0;
                        let timelineCount = 0;
                        let playlistCount = 0;
                        let playlistShareCount = 0;

                        // Get all usage for this user
                        const userUsageMap = usageMap.get(user.key);
                        if (userUsageMap) {
                            userUsageMap.forEach((usages) => {
                                usages.forEach((usage) => {
                                    // Date filtering
                                    const usageDate = dayjs(usage.date);
                                    if (
                                        filterStartDate &&
                                        usageDate.isBefore(
                                            dayjs(filterStartDate),
                                            "day"
                                        )
                                    ) {
                                        return;
                                    }
                                    if (
                                        filterEndDate &&
                                        usageDate.isAfter(
                                            dayjs(filterEndDate),
                                            "day"
                                        )
                                    ) {
                                        return;
                                    }

                                    // Count actions
                                    switch (usage.action) {
                                        case "Open":
                                            viewCount += 1;
                                            break;
                                        case "Download":
                                            downloadCount += 1;
                                            break;
                                        case "TimelineCreate":
                                            timelineCount += 1;
                                            break;
                                        case "PlaylistCreate":
                                            playlistCount += 1;
                                            break;
                                        case "PlaylistShare":
                                            let userKeysSharedTo = [
                                                ...usage.usersSharedWith,
                                            ];
                                            usage.groupsSharedWith.forEach(
                                                (groupRef) => {
                                                    const groupObject =
                                                        groupMap.get(
                                                            groupRef.key
                                                        );
                                                    if (groupObject) {
                                                        groupObject.users.forEach(
                                                            (groupUser) => {
                                                                userKeysSharedTo.push(
                                                                    groupUser.key
                                                                );
                                                            }
                                                        );
                                                    }
                                                }
                                            );
                                            playlistShareCount +=
                                                userKeysSharedTo.length;
                                            break;
                                        default:
                                            break;
                                    }
                                });
                            });
                        }

                        // Create the row
                        rowDataArray.push({
                            key: user.key,
                            name: user.fullName,
                            lastLogin: lastLogin,
                            viewCount: viewCount,
                            downloadCount: downloadCount,
                            timelineCount: timelineCount,
                            playlistCount: playlistCount,
                            playlistShareCount: playlistShareCount,
                        });
                    });

                    rowDataArray.sort((a, b) => b.viewCount - a.viewCount);

                    setHubUsers(hubUsersArray);
                    setRows(rowDataArray);
                } finally {
                    setIsLoading(false);
                }
            }, 0);
        };

        const compileTimeout = compileData();
        return () => clearTimeout(compileTimeout);
        // eslint-disable-next-line
    }, [
        props.hub,
        props.open,
        loadedFileUsage,
        loadedHubKey,
        filterStartDate,
        filterEndDate,
        excludedUserKeysFromUsersFilter,
        excludedUserKeysFromGroupsFilter,
    ]);

    function handleUserFilterClick() {
        setUserFilterDialogIsOpen(true);
    }

    function handleCloseUserFilterDialog(newExcludedUserKeys) {
        setExcludedUserKeysFromUsersFilter(newExcludedUserKeys);
        setUserFilterDialogIsOpen(false);
        setFilterIsActive(true);
    }

    function handleGroupFilterClick() {
        setGroupFilterDialogIsOpen(true);
    }

    function handleCloseGroupFilterDialog(includedGroupKeys) {
        var newExcludedUserKeys = [];
        hubUsers.forEach((user) => {
            var isInAGroup = false;

            includedGroupKeys.forEach((groupKey) => {
                const group = hubGroups.find((g) => g.key === groupKey);
                if (
                    group &&
                    group.users.filter((u) => u.key === user.key).length > 0
                ) {
                    isInAGroup = true;
                }
            });

            if (!isInAGroup && !newExcludedUserKeys.includes(user.key)) {
                newExcludedUserKeys.push(user.key);
            }
        });

        setExcludedUserKeysFromGroupsFilter(newExcludedUserKeys);
        setGroupFilterDialogIsOpen(false);
        setFilterIsActive(true);
    }

    function handleDateFilterClick() {
        setDateFilterDialogIsOpen(true);
    }

    function handleCloseDateFilterDialog(startDate, endDate) {
        setFilterStartDate(startDate);
        setFilterEndDate(endDate);
        setDateFilterDialogIsOpen(false);

        // Clearing the dates is not a filter, so it must not switch the
        // "Clear filters" button on by itself.
        if (startDate && endDate) {
            setFilterIsActive(true);
        }
    }

    function handleDownloadPress() {
        var csvString =
            "Name, Last Login, Files Accessed, Downloads, Timelines Created, Playlists Created, Playlists Shared\n";

        filteredRows.forEach((row) => {
            csvString += row.name.replace(",", "") + ", ";
            csvString += row.lastLogin.toString().replace(",", "") + ", ";
            csvString += row.viewCount + ", ";
            csvString += row.downloadCount + ", ";
            csvString += row.timelineCount + ", ";
            csvString += row.playlistCount + ", ";
            csvString += row.playlistShareCount + "\n";
        });

        downloadBlob(csvString, "export.csv", "text/csv;charset=utf-8;");
    }

    function handleClearFiltersClick() {
        setFilterStartDate(null);
        setFilterEndDate(null);
        setExcludedUserKeysFromUsersFilter([]);
        setExcludedUserKeysFromGroupsFilter([]);
        setFilterIsActive(false);
    }

    const handleClose = () => {
        props.onClose();
    };

    const filteredRows = useMemo(() => {
        const searchTerm = userSearch.trim().toLocaleLowerCase();

        if (!searchTerm) return rows;

        return rows.filter((row) =>
            row.name.toLocaleLowerCase().includes(searchTerm)
        );
    }, [rows, userSearch]);

    const showLoading =
        isLoading || loadedFileUsage === null || loadedHubKey !== hubKey;

    return (
        <>
            <Dialog
                onClose={handleClose}
                aria-labelledby="hub-user-usage-dialog-title"
                open={props.open}
                fullWidth
                maxWidth="lg"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                            maxHeight: {
                                xs: "calc(100% - 16px)",
                                sm: "calc(100% - 48px)",
                            },
                            m: { xs: 1, sm: 3 },
                            width: {
                                xs: "calc(100% - 16px)",
                                sm: "calc(100% - 48px)",
                            },
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "action.hover",
                        }}
                    >
                        <DataUsageOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="hub-user-usage-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Hub user usage
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            {props.hub?.name}
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close hub user usage"
                        onClick={handleClose}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 1.5, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    {!showLoading && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: {
                                    xs: "stretch",
                                    md: "center",
                                },
                                justifyContent: "space-between",
                                flexDirection: {
                                    xs: "column",
                                    md: "row",
                                },
                                gap: 1.5,
                                mb: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={handleUserFilterClick}
                                >
                                    Users
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleGroupFilterClick}
                                >
                                    Groups
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleDateFilterClick}
                                >
                                    Dates
                                </Button>
                                {filterIsActive && (
                                    <Button
                                        color="error"
                                        onClick={handleClearFiltersClick}
                                    >
                                        Clear filters
                                    </Button>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: { xs: "stretch", sm: "center" },
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 1,
                                }}
                            >
                                <TextField
                                    label="Search users"
                                    placeholder="Type a user name"
                                    size="small"
                                    value={userSearch}
                                    onChange={(event) =>
                                        setUserSearch(event.target.value)
                                    }
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon color="action" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: userSearch ? (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        aria-label="Clear user search"
                                                        onClick={() =>
                                                            setUserSearch("")
                                                        }
                                                    >
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null,
                                        },
                                    }}
                                    sx={{ minWidth: { sm: 240 } }}
                                />
                                <Button
                                    onClick={handleDownloadPress}
                                    variant="outlined"
                                    startIcon={<DownloadOutlinedIcon />}
                                    sx={{ flexShrink: 0 }}
                                >
                                    Download CSV
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {showLoading ? (
                        <Box
                            className={classes.loadingContainer}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2.5,
                                backgroundColor: "background.paper",
                            }}
                        >
                            <CircularProgress size={60} />
                            <Typography variant="h6">
                                Loading and compiling usage data...
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                maxHeight: {
                                    xs: "calc(100vh - 280px)",
                                    sm: "calc(100vh - 330px)",
                                },
                                overflow: "auto",
                                borderRadius: 2.5,
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Table
                                stickyHeader
                                size="small"
                                aria-label="Hub user usage"
                                sx={{
                                    minWidth: 930,
                                    "& .MuiTableCell-root": {
                                        px: { xs: 1.5, sm: 2 },
                                        py: 1.25,
                                    },
                                }}
                            >
                                <TableHead
                                    sx={{
                                        "& .MuiTableCell-root": {
                                            fontWeight: 800,
                                            backgroundColor:
                                                "background.subtle",
                                        },
                                    }}
                                >
                                    <TableRow>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            User
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Last login
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Files accessed
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Downloads
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Timelines Created
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Playlists Created
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Playlists Shared
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRows.length === 0 && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                align="center"
                                                sx={{ py: 5 }}
                                            >
                                                <Typography color="text.secondary">
                                                    No users match your search.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {filteredRows.map((row) => (
                                        <TableRow
                                            key={row.key}
                                            style={{
                                                cursor: "pointer",
                                            }}
                                        >
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.name}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.lastLogin}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.viewCount}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.downloadCount}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.timelineCount}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.playlistCount}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.playlistShareCount}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        disableElevation
                    >
                        Close
                    </Button>
                </DialogActions>
                <HubUsageUserFilterDialog
                    open={userFilterDialogIsOpen}
                    onClose={handleCloseUserFilterDialog}
                    users={hubUsers}
                    excludedUserKeys={excludedUserKeysFromUsersFilter}
                />
                <HubUsageGroupFilterDialog
                    open={groupFilterDialogIsOpen}
                    onClose={handleCloseGroupFilterDialog}
                    groups={hubGroups}
                    excludedGroupKeys={excludedUserKeysFromGroupsFilter}
                />
                <HubUsageDateFilterDialog
                    open={dateFilterDialogIsOpen}
                    onClose={handleCloseDateFilterDialog}
                    startDate={filterStartDate}
                    endDate={filterEndDate}
                />
            </Dialog>
        </>
    );
}
